import { ImageIcon, TextIcon } from '../icons';
import { PrivacyPoints, type View } from '../layout/AppShell';
import { Button } from '../ui';
import { LimitationsNote } from '../ui/Limitations';
import { FormatSupport } from './FormatSupport';

export function HomeScreen({ onNavigate }: { onNavigate: (view: View) => void }) {
  return (
    <div className="cf-enter mx-auto max-w-3xl py-6 text-center sm:py-16">
      <p className="text-brand-600 text-xs font-semibold tracking-[0.2em] uppercase">CleanFile</p>
      <h1 className="text-strong mt-4 text-4xl font-semibold tracking-tight text-balance sm:text-5xl">
        See what&rsquo;s hiding inside your files.
      </h1>
      <p className="text-muted mx-auto mt-5 max-w-xl text-[17px] leading-relaxed text-pretty">
        Inspect and remove hidden metadata, location information and unnecessary embedded data before sharing
        your files.
      </p>

      <div className="mt-9 flex flex-col items-center justify-center gap-3 sm:flex-row">
        <Button size="lg" onClick={() => onNavigate('image')} className="w-full sm:w-auto">
          <ImageIcon width={18} height={18} />
          Inspect a file
        </Button>
        <Button size="lg" variant="secondary" onClick={() => onNavigate('text')} className="w-full sm:w-auto">
          <TextIcon width={18} height={18} />
          Clean text
        </Button>
      </div>

      <p className="text-strong mt-6 text-sm font-medium">
        Your file never leaves your device. Scanning and cleaning happen locally in your browser.
      </p>
      <PrivacyPoints className="mt-4" />

      <div className="mt-14 grid gap-4 text-left sm:grid-cols-3">
        {[
          {
            title: 'Inspect first',
            body: 'Every field found in the file is listed with what it means and how much it gives away — before anything is changed.',
          },
          {
            title: 'Remove without re-encoding',
            body: 'No recompression for supported formats: metadata blocks are cut out of the file structure and the image data is copied across byte for byte, so your pixels and quality stay exactly as they were.',
          },
          {
            title: 'Check the result',
            body: 'The cleaned copy is read back from scratch and compared with the original, so you can see exactly what went and what stayed.',
          },
        ].map((item) => (
          <div key={item.title} className="surface-card p-5">
            <h2 className="text-strong text-sm font-semibold">{item.title}</h2>
            <p className="text-muted mt-2 text-[13px] leading-relaxed">{item.body}</p>
          </div>
        ))}
      </div>

      <FormatSupport />
      <LimitationsNote className="mt-6 text-left" />
    </div>
  );
}
